// Typed fetchers and query hooks for the admin Worker page (E-3 L3 M3; P2-H01..H04).
// Every call goes through authenticatedFetch. The server selects explicit
// columns only, so no payload, result or progress ever reaches these types.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from './authenticated-fetch';
import type { ApiError } from './api';

const BASE = '/api/worker';

export interface WorkerScheduleView {
  name: string;
  cron: string | null;
  everySeconds: number | null;
  tz: string;
  jobKind: string;
  lane: string;
  enabled: boolean;
  nextRunAt: string | null;
  lastEnqueuedSlot: string | null;
  lastSuccessAt: string | null;
  lastRootDeadAt: string | null;
  consecutiveFailures: number;
  consecutiveSkips: number;
  staleAfterS: number;
  alertOwner: string;
  severity: string;
  requeueSafe: boolean;
  money: boolean;
  retiredAt: string | null;
  /** ISO-8601, or 'infinity' for a pause with no end. */
  pausedUntil: string | null;
  pausedReason: string | null;
  pausedNow: boolean;
  registrySyncedAt: string | null;
}

export interface WorkerKindView {
  kind: string;
  lane: string | null;
  severity: string;
  alertOwner: string;
  money: boolean;
  requeueSafe: boolean;
  maxConcurrency: number | null;
  maxQueued: number;
  consecutiveFailures: number;
  /** ISO-8601, or 'infinity' for a boot pause (rendered as "indefinitely"). */
  pausedUntil: string | null;
  pausedReason: string | null;
  pausedNow: boolean;
  breakerOpen: boolean;
  bootDisabledReason: string | null;
  bootDisabledBy: string | null;
  bootDisabledAt: string | null;
  retiredAt: string | null;
  registrySyncedAt: string | null;
  queued: number;
  running: number;
}

export interface WorkerDeadGroupView {
  kind: string;
  errorFingerprint: string | null;
  deadCount: number;
  firstDeadAt: string | null;
  lastDeadAt: string | null;
  latestJobId: string | null;
  lastErrorName: string | null;
  lastErrorMessage: string | null;
  requeueSafe: boolean;
}

export interface WorkerDeadJobView {
  id: string;
  kind: string;
  lane: string;
  tenantId: string | null;
  attempts: number;
  maxAttempts: number;
  scheduleName: string | null;
  scheduledFor: string | null;
  dedupeKey: string | null;
  createdAt: string | null;
  finishedAt: string | null;
  errorFingerprint: string | null;
  lastErrorName: string | null;
  lastErrorMessage: string | null;
  lastErrorSqlstate: string | null;
  requeueSafe: boolean;
}

export interface WorkerAlertView {
  key: string;
  source: string;
  severity: string;
  route: string;
  subject: string;
  openedAt: string | null;
  lastSeenAt: string | null;
  lastNotifiedAt: string | null;
  resolvedAt: string | null;
  owner: string | null;
  money: boolean;
  purpose: string | null;
  subjectsOpen: number | null;
  sampleSubjects: string[];
  truncated: boolean;
  error: { sqlstate: string | null; message: string | null } | null;
  drill: boolean;
}

export interface WorkerMonitorRunView {
  assertion: string;
  firstRunAt: string | null;
  lastRunAt: string | null;
  lastOkAt: string | null;
  lastStatus: string;
}

export interface WorkerFindingView {
  id: string;
  assertion: string;
  subjectKey: string;
  tenantId: string | null;
  severity: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  resolvedAt: string | null;
  detail: Record<string, unknown>;
}

export interface WorkerLaneView {
  lane: string;
  queuedDue: number;
  queuedFuture: number;
  oldestDueAgeS: number;
  running: number;
  dead24h: number;
}

export interface WorkerDrillsView {
  kinds: {
    kind: string;
    retiredAt: string | null;
    bootDisabledReason: string | null;
    pausedNow: boolean;
  }[];
  recent: {
    id: string;
    kind: string;
    status: string;
    createdAt: string | null;
    finishedAt: string | null;
    lastErrorMessage: string | null;
  }[];
  alerts: { key: string; severity: string; openedAt: string | null; resolvedAt: string | null }[];
}

/** A successful action: the definer's code (and the drill job id). */
export interface WorkerActionResponse {
  result: string;
  jobId?: string | null;
}

export type FireDrillRequest =
  | { kind: 'system.fire-drill.throw' }
  | { kind: 'system.fire-drill.assertion'; assertion: string };

/**
 * Same ApiError shape as lib/api.ts (status, code, body), with the server's
 * own message as the error message: the server's refusal text is written for
 * operators (e.g. unsupported_tz), so it is shown as is.
 */
async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      // non-JSON error body: status-only message
    }
    const b = (body ?? {}) as { message?: unknown; code?: unknown };
    const err = new Error(
      typeof b.message === 'string' && b.message ? b.message : `Request failed: ${res.status}`,
    ) as ApiError;
    err.status = res.status;
    if (typeof b.code === 'string') err.code = b.code;
    if (body !== undefined) err.body = body;
    throw err;
  }
  return res.json() as Promise<T>;
}

function qs(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return authenticatedFetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  }).then((r) => json<T>(r));
}

const seg = (s: string) => encodeURIComponent(s);

// ── Reads ─────────────────────────────────────────────────────────────────────

export async function fetchWorkerSchedules(): Promise<{ schedules: WorkerScheduleView[] }> {
  return json(await authenticatedFetch(`${BASE}/schedules`));
}
export async function fetchWorkerKinds(): Promise<{ kinds: WorkerKindView[] }> {
  return json(await authenticatedFetch(`${BASE}/kinds`));
}
export async function fetchWorkerDeadGroups(
  p: { kind?: string } = {},
): Promise<{ groups: WorkerDeadGroupView[] }> {
  return json(await authenticatedFetch(`${BASE}/dead-jobs${qs({ kind: p.kind })}`));
}
/** `fingerprint` null selects rows that have no fingerprint. */
export async function fetchWorkerDeadJobs(p: {
  kind: string;
  fingerprint: string | null;
}): Promise<{ jobs: WorkerDeadJobView[] }> {
  return json(
    await authenticatedFetch(
      `${BASE}/dead-jobs/rows${qs({ kind: p.kind, fingerprint: p.fingerprint ?? 'none' })}`,
    ),
  );
}
export async function fetchWorkerAlerts(
  p: { state?: 'open' | 'all' } = {},
): Promise<{ alerts: WorkerAlertView[] }> {
  return json(await authenticatedFetch(`${BASE}/alerts${qs({ state: p.state })}`));
}
export async function fetchWorkerMonitorRuns(): Promise<{ runs: WorkerMonitorRunView[] }> {
  return json(await authenticatedFetch(`${BASE}/alerts/runs`));
}
export async function fetchWorkerFindings(
  p: { state?: 'open' | 'all'; assertion?: string; tenantId?: string } = {},
): Promise<{ findings: WorkerFindingView[] }> {
  return json(
    await authenticatedFetch(
      `${BASE}/findings${qs({ state: p.state, assertion: p.assertion, tenantId: p.tenantId })}`,
    ),
  );
}
export async function fetchWorkerQueue(): Promise<{ lanes: WorkerLaneView[] }> {
  return json(await authenticatedFetch(`${BASE}/queue`));
}
export async function fetchWorkerDrills(): Promise<WorkerDrillsView> {
  return json(await authenticatedFetch(`${BASE}/drills`));
}

// ── Actions (WORKER_OPERATE; each one definer + audit row server-side) ──────

export const pauseWorkerSchedule = (name: string, until: string, reason: string) =>
  post<WorkerActionResponse>(`/schedules/${seg(name)}/pause`, { until, reason });
export const unpauseWorkerSchedule = (name: string) =>
  post<WorkerActionResponse>(`/schedules/${seg(name)}/unpause`);
export const runWorkerScheduleNow = (name: string) =>
  post<WorkerActionResponse>(`/schedules/${seg(name)}/run-now`);
export const pauseWorkerKind = (kind: string, until: string, reason: string) =>
  post<WorkerActionResponse>(`/kinds/${seg(kind)}/pause`, { until, reason });
export const unpauseWorkerKind = (kind: string) =>
  post<WorkerActionResponse>(`/kinds/${seg(kind)}/unpause`);
export const requeueWorkerDeadJob = (id: string) =>
  post<WorkerActionResponse>(`/dead-jobs/${seg(id)}/requeue`);
export const enqueueWorkerFireDrill = (body: FireDrillRequest) =>
  post<WorkerActionResponse>('/drills', body);

// ── Query hooks ──────────────────────────────────────────────────────────────

export const workerQueryKeys = {
  all: ['worker'] as const,
  schedules: ['worker', 'schedules'] as const,
  kinds: ['worker', 'kinds'] as const,
  deadGroups: ['worker', 'dead-groups'] as const,
  deadJobs: (kind: string, fingerprint: string | null) =>
    ['worker', 'dead-jobs', kind, fingerprint] as const,
  alerts: (state: 'open' | 'all') => ['worker', 'alerts', state] as const,
  runs: ['worker', 'runs'] as const,
  findings: (state: 'open' | 'all', assertion: string) =>
    ['worker', 'findings', state, assertion] as const,
  queue: ['worker', 'queue'] as const,
  drills: ['worker', 'drills'] as const,
};

export const useWorkerSchedules = () =>
  useQuery({ queryKey: workerQueryKeys.schedules, queryFn: fetchWorkerSchedules });
export const useWorkerKinds = () =>
  useQuery({ queryKey: workerQueryKeys.kinds, queryFn: fetchWorkerKinds });
export const useWorkerDeadGroups = () =>
  useQuery({ queryKey: workerQueryKeys.deadGroups, queryFn: () => fetchWorkerDeadGroups() });
export const useWorkerDeadJobs = (kind: string | null, fingerprint: string | null) =>
  useQuery({
    queryKey: workerQueryKeys.deadJobs(kind ?? '', fingerprint),
    queryFn: () => fetchWorkerDeadJobs({ kind: kind ?? '', fingerprint }),
    enabled: kind !== null,
  });
export const useWorkerAlerts = (state: 'open' | 'all') =>
  useQuery({
    queryKey: workerQueryKeys.alerts(state),
    queryFn: () => fetchWorkerAlerts({ state }),
  });
export const useWorkerMonitorRuns = () =>
  useQuery({ queryKey: workerQueryKeys.runs, queryFn: fetchWorkerMonitorRuns });
export const useWorkerFindings = (state: 'open' | 'all', assertion: string) =>
  useQuery({
    queryKey: workerQueryKeys.findings(state, assertion),
    queryFn: () => fetchWorkerFindings(assertion ? { state, assertion } : { state }),
  });
export const useWorkerQueue = () =>
  useQuery({ queryKey: workerQueryKeys.queue, queryFn: fetchWorkerQueue });
export const useWorkerDrills = () =>
  useQuery({ queryKey: workerQueryKeys.drills, queryFn: fetchWorkerDrills });

/** Runs one Worker action and refreshes every Worker view afterwards (refusals included). */
export function useWorkerAction<A extends unknown[]>(
  fn: (...args: A) => Promise<WorkerActionResponse>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: A) => fn(...args),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: workerQueryKeys.all });
    },
  });
}
